import { useEffect, useCallback, useRef, useMemo } from 'react'
import { useDrawer } from '@/components/ui/drawer/DrawerProvider'

interface UseDrawerFormOptions<T = unknown> {
  drawerId: string
  autoSave?: boolean
  autoSaveInterval?: number
  confirmClose?: boolean
  confirmCloseMessage?: string
  onAutoSave?: (data: T) => Promise<void>
  restoreFromDraft?: boolean
  onFormChange?: (isDirty: boolean) => void
}

export function useDrawerForm<T extends Record<string, unknown>>(
  formData: T,
  setFormData: (data: T | ((prev: T) => T)) => void,
  options: UseDrawerFormOptions<T>
) {
  const { updateDrawer } = useDrawer()
  const { 
    drawerId, 
    autoSave = false,
    autoSaveInterval = 30000, // 30 seconds
    confirmClose = true, 
    confirmCloseMessage,
    onAutoSave,
    restoreFromDraft = true,
    onFormChange
  } = options

  const initialDataRef = useRef<T>(formData)
  const autoSaveTimerRef = useRef<NodeJS.Timeout | null>(null)
  const lastSavedDataRef = useRef<T>(formData)

  // Check if form is dirty
  const isDirty = useMemo(() => {
    return JSON.stringify(formData) !== JSON.stringify(initialDataRef.current)
  }, [formData])

  // Auto-save to localStorage
  const saveToLocalStorage = useCallback(() => {
    const draftKey = `drawer-draft-${drawerId}`
    localStorage.setItem(draftKey, JSON.stringify(formData))
    localStorage.setItem(`${draftKey}-timestamp`, new Date().toISOString())
  }, [drawerId, formData])

  // Clear draft from localStorage
  const clearDraft = useCallback(() => {
    const draftKey = `drawer-draft-${drawerId}`
    localStorage.removeItem(draftKey)
    localStorage.removeItem(`${draftKey}-timestamp`)
  }, [drawerId])

  // Restore from draft on mount
  useEffect(() => {
    if (restoreFromDraft) {
      const draftKey = `drawer-draft-${drawerId}`
      const draft = localStorage.getItem(draftKey)
      const timestamp = localStorage.getItem(`${draftKey}-timestamp`)
      
      if (draft && timestamp) {
        const draftAge = Date.now() - new Date(timestamp).getTime()
        const maxAge = 24 * 60 * 60 * 1000 // 24 hours
        
        if (draftAge < maxAge) {
          try {
            const draftData = JSON.parse(draft)
            setFormData(draftData)
            console.log(`Restored draft from ${new Date(timestamp).toLocaleString()}`)
          } catch (error) {
            console.error('Failed to restore draft:', error)
            clearDraft()
          }
        } else {
          // Draft is too old, clear it
          clearDraft()
        }
      }
    }
  }, [restoreFromDraft, drawerId, setFormData, clearDraft])

  // Update drawer state when form dirty state changes
  useEffect(() => {
    const dirty = isDirty
    updateDrawer(drawerId, {
      hasUnsavedChanges: dirty,
      confirmClose,
      confirmCloseMessage: confirmCloseMessage || 'You have unsaved changes. Are you sure you want to close?',
    })
    onFormChange?.(dirty)
  }, [formData, isDirty, drawerId, confirmClose, confirmCloseMessage, updateDrawer, onFormChange])

  // Auto-save functionality
  useEffect(() => {
    if (autoSave && isDirty) {
      // Clear existing timer
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }

      // Save to localStorage immediately
      saveToLocalStorage()

      // Set up auto-save timer for API call
      if (onAutoSave) {
        autoSaveTimerRef.current = setTimeout(async () => {
          try {
            await onAutoSave(formData)
            lastSavedDataRef.current = formData
            console.log('Auto-saved successfully')
          } catch (error) {
            console.error('Auto-save failed:', error)
          }
        }, autoSaveInterval)
      }
    }

    return () => {
      if (autoSaveTimerRef.current) {
        clearTimeout(autoSaveTimerRef.current)
      }
    }
  }, [formData, autoSave, autoSaveInterval, isDirty, onAutoSave, saveToLocalStorage])

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setFormData(initialDataRef.current)
    clearDraft()
    updateDrawer(drawerId, { hasUnsavedChanges: false })
  }, [setFormData, clearDraft, drawerId, updateDrawer])

  // Mark form as saved
  const markAsSaved = useCallback(() => {
    initialDataRef.current = formData
    lastSavedDataRef.current = formData
    clearDraft()
    updateDrawer(drawerId, { hasUnsavedChanges: false })
  }, [formData, clearDraft, drawerId, updateDrawer])

  // Rebase the pristine snapshot after async data loads (edit drawers fetch
  // their record after mount, so the mount-time snapshot is an empty form and
  // the drawer would otherwise open permanently "dirty").
  const setInitialData = useCallback((data: T) => {
    initialDataRef.current = data
    lastSavedDataRef.current = data
    updateDrawer(drawerId, { hasUnsavedChanges: false })
  }, [drawerId, updateDrawer])

  // Close without saving
  const closeWithoutSaving = useCallback(() => {
    resetForm()
    updateDrawer(drawerId, { hasUnsavedChanges: false })
  }, [resetForm, drawerId, updateDrawer])

  // Get form state
  const getFormState = useCallback(() => ({
    isDirty,
    hasAutoSaveData: lastSavedDataRef.current !== initialDataRef.current,
    lastSaved: lastSavedDataRef.current,
    initial: initialDataRef.current,
    current: formData,
  }), [formData, isDirty])

  return {
    isDirty,
    resetForm,
    markAsSaved,
    setInitialData,
    closeWithoutSaving,
    clearDraft,
    getFormState,
    saveToLocalStorage,
  }
}